import { useEffect, useState } from 'react'
import BottomSheetContent from './BottomSheetContent'
import LeftLeafSVG from '../../icons/LeftLeafSVG'
import RightLeafSVG from '../../icons/RightLeafSVG'
import type { EliteOnboardingStatusResponse } from '../../types/misc.types'
import { lightImpactVibrate, postCTA } from '../../utils/appBridge'
import { useNewEliteLandingAnalyticsScreen } from '../../features/new-elite-landing/analytics/NewEliteLandingAnalyticsContext'
import {
  NewEliteLandingBottomSheetName,
  NewEliteLandingEventId,
  trackNewEliteBottomSheetLoaded,
  trackNewEliteButtonTap,
  trackNewEliteUserClicked
} from '../../features/new-elite-landing/analytics/newEliteLandingAnalytics'
import { convertEpochToDate, roundOffAndFormatNumberWithCurrency } from '../../utils/utils'
import type { FamilyOnboardingViewProps } from '../../features/new-elite-landing/pages/family/familyOnboardingViewProps'

export function EliteYearlyUpgradeBottomSheet({
  familyConfig,
  eliteOnboardingStatus,
  onDismiss
}: {
  familyConfig?: FamilyOnboardingViewProps
  eliteOnboardingStatus?: EliteOnboardingStatusResponse
  onDismiss: () => void
}) {
  const analyticsScreen = useNewEliteLandingAnalyticsScreen()
  const [adjustmentExpanded, setAdjustmentExpanded] = useState(false)

  useEffect(() => {
    trackNewEliteBottomSheetLoaded(analyticsScreen, NewEliteLandingBottomSheetName.EliteYearlyUpgrade)
  }, [analyticsScreen])
  const cta = eliteOnboardingStatus?.subscriptionDetails?.upgradeDetails?.upgradeCta
  const upgradePricingDetails = eliteOnboardingStatus?.subscriptionDetails?.upgradeDetails?.upgradePricingDetails
  const {
    upgradePlanPrice,
    adjustmentFromCurrentPlan,
    totalPayableAmount,
    newPlanValidTill,
    offerType,
    tenureDiff,
    familyDiscount,
    percentageSavings
  } = upgradePricingDetails ?? {}

  const familyOrangeStrip = familyConfig?.isFamilyMode ? familyConfig.familyOrangeStripText : null
  const offerBannerText = getUpgradeOfferBannerText(offerType, percentageSavings, tenureDiff)
  const showPricingBanner = Boolean(familyOrangeStrip) || Boolean(offerBannerText)

  const handleContinue = () => {
    lightImpactVibrate()
    trackNewEliteUserClicked(analyticsScreen, NewEliteLandingEventId.UpgradeContinuePayment)
    if (cta?.url) {
      postCTA(JSON.stringify(cta))
    }
    onDismiss()
  }

  return (
    <BottomSheetContent
      onDismiss={onDismiss}
      headingComponent={
        <div className='px-5 pb-1'>
          <p className='text-left text-sm font-normal text-[#232323]'>Switching to</p>
          <div className='mt-2 flex items-center justify-start'>
            <LeftLeafSVG color='#BB4F22' size={22} />
            <span className='text-[22px] font-medium leading-[1.15] text-[#BB4F22]'>Elite Yearly</span>
            <RightLeafSVG color='#BB4F22' size={22} />
          </div>
        </div>
      }
      bottomSheetBodyClassName='pb-2'
      bottomSheetCtas={
        <div
          onClick={handleContinue}
          className='flex h-14 w-full cursor-pointer items-center justify-center rounded-xl bg-[#232323] text-base font-medium text-white'
        >
          Continue to payment
        </div>
      }
    >
      <div className='overflow-hidden rounded-2xl border border-[#EDE6E3] bg-white mt-6'>
        {showPricingBanner ? (
          <div className='bg-[#FFEBCF] px-4 py-3 text-center text-13 font-medium leading-[1.35] text-[#65321D]'>
            {familyOrangeStrip ? (
              <div className={offerBannerText ? 'pb-2' : ''}>
                <p className='text-[11px] leading-[1.2] tracking-[0.04em]'>{familyOrangeStrip.headline}</p>
                <p className='mt-1 text-13 leading-[1.4] font-medium'>{familyOrangeStrip.subline}</p>
              </div>
            ) : offerBannerText ? <p>{offerBannerText}</p> : null}
          </div>
        ) : null}
        <div className='px-4 py-3'>
          <div className='flex items-center justify-between gap-3 py-2 text-sm'>
            <span className=' text-grey40'>Elite yearly</span>
            <span className=' text-grey40'>
              {roundOffAndFormatNumberWithCurrency(upgradePlanPrice ?? 0)}
            </span>
          </div>
          {familyConfig?.isFamilyMode && (familyDiscount ?? 0) > 0 ? (
            <div className='flex items-center justify-between gap-3 py-2 text-sm'>
              <span className='text-grey40'>Family discount</span>
              <span className='text-[#1B7F4C]'>
                -{roundOffAndFormatNumberWithCurrency(familyDiscount ?? 0)}
              </span>
            </div>
          ) : null}
          {
            offerType === 'TENURE_BASED' ? (
              <div className='flex items-center justify-between gap-3 py-2 text-sm'>
                <span className=' text-grey40'>+{tenureDiff} extra {tenureDiff === 1 ? 'month' : 'months'}</span>
                <span className='text-[#1B7F4C]'>FREE</span>
              </div>
            ) : null}
          <div
            onClick={() => {
              const next = !adjustmentExpanded
              trackNewEliteButtonTap(analyticsScreen, NewEliteLandingEventId.UpgradeAdjustToggle, {
                buttonLabel: next ? 'expanded' : 'collapsed'
              })
              setAdjustmentExpanded(next)
            }}
            className='flex cursor-pointer items-start justify-between gap-3 py-2 text-sm'
          >
            <div className='flex min-w-0 flex-1 items-center gap-1'>
              <span className='text-grey40'>Adjusting current plan</span>
              <span
                className={`mt-0.5 inline-flex shrink-0 items-center justify-center text-grey40 transition-transform ${adjustmentExpanded ? 'rotate-180' : ''
                  }`}
              >
                <svg xmlns='http://www.w3.org/2000/svg' width='12' height='10' viewBox='0 0 12 10' fill='none' aria-hidden>
                  <path
                    d='M2 3L6 7L10 3'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </span>
            </div>
            <span className='shrink-0 text-[#1B7F4C]'>
              - {roundOffAndFormatNumberWithCurrency(adjustmentFromCurrentPlan ?? 0)}
            </span>
          </div>
          {adjustmentExpanded ? (
            <p className='pb-2 pl-0 text-13 leading-[1.4] text-grey40'>
              We credit the unused portion of your current Elite plan toward this upgrade.
            </p>
          ) : null}
          <div className='my-3 border-t border-[#EDE6E3]' />
          <div className='flex items-center justify-between gap-3 pb-1 text-13'>
            <span className='text-grey40'>You pay just</span>
            <span className='text-[#232323] text-[18px]'>
              {roundOffAndFormatNumberWithCurrency(totalPayableAmount ?? 0)}
              <span className='text-[11px] font-normal text-grey40'>+GST</span>
            </span>
          </div>
        </div>
      </div>

      <div className='mt-4 rounded-2xl border border-[#E6E6E6] bg-white px-4 py-4'>
        <div className='flex items-start justify-between gap-3'>
          <span className='text-sm text-[#232323]'>New plan validity</span>
          <span className='text-right text-sm text-[#232323]'>
            {newPlanValidTill ? convertEpochToDate(newPlanValidTill, '%d %b %Y') : '—'}
          </span>
        </div>
        <p className='mt-2 text-13 font-normal leading-[1.3] text-grey40'>New plan starts today</p>
      </div>
    </BottomSheetContent>
  )
}

function getUpgradeOfferBannerText(
  offerType: string | undefined,
  percentageSavings: number | undefined,
  tenureDiff: number | undefined
): string | null {
  if (offerType === 'PRICE_BASED') {
    return `SAVE ${percentageSavings?.toFixed(0) ?? 0}% by switching to Yearly plan`
  }
  if (offerType === 'TENURE_BASED') {
    return `GET ${tenureDiff} EXTRA ${tenureDiff === 1 ? 'MONTH' : 'MONTHS'} with yearly plan`
  }
  return null
}
